/*
 * Copyright 2026 North Carolina State University
 */

// Single place that decides where clicking a notification lands. The home
// dashboard, the bell panel and the per-project notification lists all route
// through this so the same notification opens the same screen wherever it is
// clicked.
//
// backend/services/notificationService.js has a mirror of this logic
// (resolveEmailViewPath) for the "View" link in notification emails — keep the
// two in step.

// Invoice lifecycle tab an event most likely lands in. This is only a fallback:
// the invoicing screen re-resolves the tab from the invoice's live status once
// it has loaded, because an invoice usually moves on after the notification was
// sent (created -> billed -> signed -> paid).
const INVOICE_EVENT_TAB = {
  invoice_created: 'invoiced',
  invoice_edited: 'invoiced',
  invoice_deleted: 'invoiced',
  invoice_billed: 'billed',
  invoice_payer_added: 'billed',
  invoice_status_changed: 'billed',
  invoice_disputed: 'dispute',
  invoice_dispute_resolved: 'billed',
  invoice_signed: 'signed',
  payment_recorded: 'paid',
  payment_edited: 'paid',
  payment_deleted: 'billed',
};

const REVISION_EVENTS = ['application_revised', 'revision_requested'];

// Events about the application/project itself. Listed explicitly because a few
// of them are filed under another category — a termination is raised through a
// request, but the notification is about the project, not the request.
const PROJECT_EVENTS = new Set([
  'application_submitted',
  'application_approved',
  'application_rejected',
  'revision_requested',
  'application_revised',
  'project_started',
  'project_completed',
  'project_terminated',
  'project_renewed',
  'project_renewal_confirmation',
]);

const isFacilityChatEvent = (eventType = '') =>
  eventType === 'facility_chat_message' || eventType === 'facility_chat_member_change';

const isRequestEvent = (eventType = '', category = '') =>
  category === 'request' || eventType.startsWith('request_');

const isInvoiceEvent = (eventType = '', category = '') =>
  category === 'invoice' || eventType.startsWith('invoice_') || eventType.startsWith('payment_');

/**
 * Resolve the route a notification should open.
 *
 * @param {object} notification  A notification from GET /api/notifications.
 * @param {boolean} asAdmin      Open the admin-side screen rather than the
 *                               member-side project view.
 * @returns {string|null}        Path to navigate to, or null if there is
 *                               nowhere meaningful to go.
 */
export const getNotificationRoute = (notification, asAdmin = false) => {
  if (!notification) return null;

  const eventType = notification.event_type || '';
  const category = notification.category || '';

  if (notification.facility_chat || isFacilityChatEvent(eventType)) {
    return asAdmin ? '/admin/chats' : '/chats';
  }

  const project = notification.project;
  const projectId = project?._id || project;
  if (!projectId) return null;

  const relatedEntity = notification.related_entity;
  const entityId = relatedEntity?.entity_id || null;

  const isProjectEvent = PROJECT_EVENTS.has(eventType);

  // Requests: an admin goes straight to that request's review page; a project
  // member goes to the project's Requests tab.
  if (!isProjectEvent && isRequestEvent(eventType, category)) {
    if (asAdmin) {
      return relatedEntity?.entity_type === 'request' && entityId
        ? `/admin/requests/${entityId}`
        : '/admin/requests';
    }
    return `/projects/view/${projectId}?tab=requests`;
  }

  // Invoices: an admin goes to the accounting screen with the project already
  // typed into the search box and the invoice's lifecycle tab open; a project
  // member goes to the project's Invoices tab.
  if (!isProjectEvent && isInvoiceEvent(eventType, category)) {
    if (asAdmin) {
      const params = new URLSearchParams();
      params.set(
        'section',
        project?.application_type === 'resource_only' ? 'resource-invoicing' : 'invoicing'
      );
      const projectLabel = project?.project_title || project?.project_id || '';
      if (projectLabel) params.set('search', projectLabel);
      if (relatedEntity?.entity_type === 'invoice' && entityId) params.set('invoiceId', entityId);
      params.set('tab', INVOICE_EVENT_TAB[eventType] || 'billed');
      return `/admin/accounting?${params.toString()}`;
    }
    return `/projects/view/${projectId}?tab=invoices`;
  }

  // Everything else is about the application/project itself.
  if (asAdmin) {
    // Revision-cycle events name the tab explicitly; for the rest the review
    // screen picks the tab from the project's current status.
    if (REVISION_EVENTS.includes(eventType)) {
      return `/admin/application?applicationId=${projectId}&status=${encodeURIComponent('Revision Required')}`;
    }
    return `/admin/application?applicationId=${projectId}`;
  }

  if (REVISION_EVENTS.includes(eventType)) {
    return `/projects/revise/${projectId}`;
  }

  return `/projects/view/${projectId}`;
};

export default getNotificationRoute;
